<template>
  <div class="purchase-page-wrapper">
    <div class="purchase-page-container">
      <v-alert v-if="!canCreate" type="warning" text dense border="left" class="mb-3 shrink-0">
        {{ $t("waste_no_permission_create") }}
      </v-alert>

      <!-- MAIN 2-COLUMN WORKSPACE (Purchase Items Page Pattern) -->
      <v-row class="fill-height ma-0" no-gutters>
        <!-- LEFT PANEL: Product / Ingredient Catalog with Search & Source Switcher -->
        <v-col cols="12" md="4" lg="3" class="d-flex flex-column pr-md-3 pb-3 pb-md-0 fill-height">
          <v-card
            class="ap-card product-card flex-grow-1 d-flex flex-column fill-height"
            outlined
            elevation="0"
          >
            <v-card-title class="py-3 px-4 d-flex align-center justify-space-between border-bottom flex-shrink-0 ap-card-title">
              <div class="d-flex align-center" style="gap: 10px;">
                <span class="ap-card-accent"></span>
                <span class="font-weight-bold text-subtitle-2">{{ $t("waste_pick_items") }}</span>
              </div>
              <v-chip x-small color="primary" outlined class="font-weight-bold">
                {{ pickerRows.length }} {{ $t("items") }}
              </v-chip>
            </v-card-title>

            <v-card-text class="pt-2 px-3 pb-2 d-flex flex-column flex-grow-1 overflow-hidden" style="min-height: 0;">
              <!-- Search & Source Switcher -->
              <div class="filter-controls mb-2 flex-shrink-0">
                <TextSearch
                  ref="search"
                  v-model="search"
                  placeholder="waste_search_scan"
                  @search="onSearch"
                  @onClear="clearSearch"
                />

                <!-- Category / Source Toggle Tabs (Matches /report-branches .br-tabs style) -->
                <div class="waste-source-tabs-wrap mt-2">
                  <div class="br-tabs">
                    <div
                      class="br-tab"
                      :class="{ 'br-tab--active': source === 'products' }"
                      @click="source = 'products'"
                    >
                      <v-icon left x-small class="mr-1" :color="source === 'products' ? 'primary' : '#94a3b8'">fal fa-box</v-icon>
                      {{ $t("waste_tab_products") }}
                    </div>
                    <div
                      class="br-tab"
                      :class="{ 'br-tab--active': source === 'ingredients' }"
                      @click="source = 'ingredients'"
                    >
                      <v-icon left x-small class="mr-1" :color="source === 'ingredients' ? 'primary' : '#94a3b8'">fal fa-mortar-pestle</v-icon>
                      {{ $t("waste_tab_ingredients") }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Product / Item List Container -->
              <div class="product-list-container flex-grow-1 overflow-y-auto pr-1" :aria-busy="loadingItems">
                <!-- Skeletons when loading -->
                <template v-if="loadingItems">
                  <v-skeleton-loader
                    v-for="n in 6"
                    :key="n"
                    type="list-item-avatar-two-line"
                    class="my-1"
                  />
                </template>

                <!-- Item Cards -->
                <template v-else-if="pickerRows.length">
                  <div
                    v-for="row in pickerRows"
                    :key="row.key"
                    class="product-item-card mb-2"
                    :class="{
                      'is-in-cart': inRecord(row.key),
                      'is-disabled': !canCreate
                    }"
                    @click="addRow(row)"
                    :title="$t('click_to_add_product')"
                  >
                    <!-- Left Area: Thumb + Details -->
                    <div class="product-card-left">
                      <div class="product-thumb-box">
                        <app-img
                          :img="row.img"
                          width="46"
                          height="46"
                          class="product-thumb-img"
                        />
                        <span v-if="inRecord(row.key)" class="cart-count-badge">
                          <v-icon x-small color="white">fal fa-check</v-icon>
                        </span>
                      </div>

                      <div class="product-info-box">
                        <div class="product-name-text text-truncate">
                          {{ row.name }}
                        </div>

                        <div class="product-sub-row">
                          <span v-if="codeLabel(row)" class="product-code-mono">{{ codeLabel(row) }}</span>
                          <span v-if="codeLabel(row)" class="product-dot-sep">•</span>
                          <!-- Stock status pill -->
                          <span v-if="!(Number(row.stock) > 0)" class="stock-pill stock-pill-out">
                            {{ $t("out_of_stock") }} (0)
                          </span>
                          <span v-else-if="Number(row.stock) <= 10" class="stock-pill stock-pill-low">
                            {{ $t("low_stock") }} ({{ wasteNumber(row.stock, 3) }})
                          </span>
                          <span v-else class="stock-pill-normal">
                            {{ $t("stock") }}: {{ wasteNumber(row.stock, 3) }}
                            <template v-if="row.unit_name || row.unit">{{ row.unit_name || row.unit }}</template>
                          </span>
                        </div>

                        <div class="product-price-row">
                          <span class="buy-price-val">
                            {{ wasteNumber(row.price_buy || row.unit_cost) }} ₭
                          </span>
                          <v-chip
                            v-if="row.variant_label"
                            x-small
                            label
                            outlined
                            color="primary"
                            class="ml-1"
                          >
                            {{ row.variant_label }}
                          </v-chip>
                          <WasteItemTypeTag :type="row.item_type" />
                        </div>
                      </div>
                    </div>

                    <!-- Right Area: Add Button -->
                    <button
                      type="button"
                      class="product-add-btn"
                      :class="{ 'btn-in-cart': inRecord(row.key) }"
                      :disabled="!canCreate"
                      :title="$t('add')"
                      @click.stop="addRow(row)"
                    >
                      <v-icon small class="add-btn-icon" :color="inRecord(row.key) ? 'white' : ''">
                        {{ inRecord(row.key) ? "fal fa-check" : "fal fa-plus" }}
                      </v-icon>
                    </button>
                  </div>
                </template>

                <!-- Empty State -->
                <div v-else class="ps-empty text-center py-8">
                  <v-icon size="48" class="ps-empty-icon mb-2">fal fa-box-open</v-icon>
                  <div class="ps-empty-text font-weight-bold">
                    {{ source === "products" ? $t("no_products_found") : $t("no_ingredients_found") }}
                  </div>
                  <div v-if="source === 'products'" class="ps-empty-subtext mt-1 text-caption">
                    {{ $t("waste_products_tracked_only") }}
                  </div>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- RIGHT PANEL: Waste Cart Table & Financial HUD -->
        <v-col cols="12" md="8" lg="9" class="d-flex flex-column pl-md-0">
          <v-card
            class="ap-card purchase-main-card flex-grow-1 d-flex flex-column fill-height"
            outlined
            elevation="0"
          >
            <!-- Cart Header -->
            <v-card-title class="py-3 px-4 border-bottom d-flex align-center justify-space-between flex-shrink-0 ap-card-title">
              <div class="d-flex align-center flex-wrap" style="gap: 10px;">
                <span class="ap-card-accent"></span>
                <span class="font-weight-bold text-subtitle-2">{{ $t("waste_new_record") }}</span>
                <span class="ap-card-subtitle d-none d-sm-inline">
                  ({{ $t("waste_management_sub") }})
                </span>
              </div>

              <div class="d-flex align-center flex-wrap" style="gap: 8px;">
                <v-btn
                  v-if="lines.length > 0"
                  text
                  small
                  color="error"
                  class="font-weight-bold text-caption rounded-lg"
                  @click="clearAllLines"
                >
                  <v-icon left small>fal fa-trash</v-icon>
                  {{ $t("clear_selected") }}
                </v-btn>

                <v-btn
                  outlined
                  small
                  rounded
                  color="primary"
                  class="text-none font-weight-medium"
                  to="/waste-request"
                >
                  <v-icon left x-small>fal fa-list</v-icon>
                  {{ $t("waste_view_requests") }}
                </v-btn>
              </div>
            </v-card-title>

            <!-- Scrollable Content Table -->
            <div class="cart-content-area flex-grow-1 overflow-auto">
              <v-data-table
                :headers="recordHeaders"
                :items="lines"
                item-key="key"
                :expanded.sync="expandedLines"
                hide-default-footer
                class="purchase-table elevation-0"
                fixed-header
                :items-per-page="50"
              >
                <!-- Row Index -->
                <template slot="item.index" slot-scope="{ index }">
                  <span class="ps-row-index">{{ index + 1 }}</span>
                </template>

                <!-- Product / Item Name & Metadata -->
                <template slot="item.name" slot-scope="{ item }">
                  <div
                    class="d-flex align-center py-2"
                    :class="{ 'is-scanned': item.key === scannedKey }"
                  >
                    <span class="ps-row-thumb mr-3 shrink-0">
                      <v-avatar tile size="38" class="rounded-lg ps-thumb-avatar">
                        <app-img :img="item.img" />
                      </v-avatar>
                    </span>
                    <div class="ps-row-name min-w-0">
                      <span class="ps-product-name text-truncate" :title="item.name">
                        {{ item.name }}
                      </span>
                      <div class="d-flex align-center flex-wrap" style="gap: 6px;">
                        <span v-if="item.sub" class="ps-product-code">{{ item.sub }}</span>
                        <v-chip
                          v-if="item.variant_label"
                          x-small
                          label
                          outlined
                          color="primary"
                        >
                          {{ item.variant_label }}
                        </v-chip>
                        <WasteItemTypeTag :type="item.item_type" />
                      </div>

                      <!-- Stock calculation preview -->
                      <div class="stock-change-hint d-flex align-center text-caption mt-1">
                        <span class="grey--text text--darken-1">{{ $t("stock") }}: {{ wasteNumber(item.stock, 3) }}</span>
                        <v-icon x-small class="mx-1">fal fa-long-arrow-right</v-icon>
                        <span :class="stockAfter(item) < 0 ? 'error--text font-weight-bold' : 'font-weight-medium'">
                          {{ wasteNumber(stockAfter(item), 3) }}
                        </span>
                        <span v-if="item.unit" class="ml-1 grey--text">{{ item.unit }}</span>
                      </div>

                      <!-- Stock alert if negative -->
                      <div v-if="stockAfter(item) < 0" class="error--text text-caption font-weight-bold d-flex align-center mt-1" role="alert">
                        <v-icon x-small color="error" class="mr-1">fal fa-exclamation-triangle</v-icon>
                        {{ $t("waste_stock_after_negative", { stock: wasteNumber(item.stock, 3) }) }}
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Direction (Out / In) for Wrong Usage -->
                <template slot="item.direction" slot-scope="{ item }">
                  <div class="d-flex justify-center">
                    <v-btn-toggle
                      v-model="item.direction"
                      mandatory
                      dense
                      class="ps-status-toggle"
                    >
                      <v-btn value="out" x-small class="text-none">
                        <v-icon left x-small>fal fa-minus</v-icon>
                        {{ $t("waste_direction_out") }}
                      </v-btn>
                      <v-btn value="in" x-small class="text-none">
                        <v-icon left x-small>fal fa-plus</v-icon>
                        {{ $t("waste_direction_in") }}
                      </v-btn>
                    </v-btn-toggle>
                  </div>
                </template>

                <!-- Qty Stepper / Input -->
                <template slot="item.qty" slot-scope="{ item }">
                  <div class="d-flex flex-column align-center justify-center">
                    <div class="ps-qty-stepper" :class="{ 'error-border': showErrors && !!qtyError(item) }">
                      <button
                        type="button"
                        class="ps-qty-btn"
                        @click="decreaseQty(item)"
                      >
                        <v-icon x-small>fal fa-minus</v-icon>
                      </button>
                      <input
                        type="number"
                        v-model.number="item.qty"
                        :min="item.integerOnly ? 1 : 0.001"
                        :step="item.integerOnly ? 1 : 0.001"
                        class="ps-qty-input"
                      />
                      <button
                        type="button"
                        class="ps-qty-btn ps-qty-btn--plus"
                        @click="increaseQty(item)"
                      >
                        <v-icon x-small>fal fa-plus</v-icon>
                      </button>
                    </div>
                    <span v-if="showErrors && qtyError(item)" class="error--text text-caption mt-1" style="font-size: 10px !important;">
                      {{ qtyError(item) }}
                    </span>
                  </div>
                </template>

                <!-- Waste Type Selector -->
                <template slot="item.waste_type" slot-scope="{ item }">
                  <div style="min-width: 150px;">
                    <TextSelect
                      v-model="item.waste_type"
                      :items="wasteTypeItems"
                      label="waste_type"
                      text="text"
                      itemValue="value"
                      :return-object="false"
                      :searchable="false"
                      :placeholder="false"
                      :dense="true"
                      :error-messages="showErrors && !item.waste_type ? [$t('waste_select_type_required')] : []"
                      class="waste-type-select rounded-lg text-caption"
                    />
                  </div>
                </template>

                <!-- Estimated Cost -->
                <template slot="item.cost" slot-scope="{ item }">
                  <span
                    class="ps-total-value text-right d-block"
                    :class="{ 'success--text': item.direction === 'in' }"
                  >
                    {{ item.direction === "in" ? "−" : "" }}{{ wasteNumber(lineCost(item)) }} ₭
                  </span>
                </template>

                <!-- Actions: Expand details & Delete -->
                <template slot="item.actions" slot-scope="{ item }">
                  <div class="ps-row-actions justify-center">
                    <button
                      type="button"
                      class="ps-act"
                      :class="{ 'primary--text primary-border': isLineExpanded(item) }"
                      :title="$t('waste_more_details')"
                      :aria-expanded="String(isLineExpanded(item))"
                      @click="toggleLineExpand(item)"
                    >
                      <v-icon x-small>
                        {{ isLineExpanded(item) ? "fal fa-chevron-up" : "fal fa-chevron-down" }}
                      </v-icon>
                    </button>
                    <button
                      type="button"
                      class="ps-act ps-act--danger"
                      :title="$t('delete')"
                      @click="removeLine(item.key)"
                    >
                      <v-icon x-small>fal fa-trash</v-icon>
                    </button>
                  </div>
                </template>

                <!-- Expanded Sub-row for extra parameters -->
                <template v-slot:expanded-item="{ headers, item }">
                  <td :colspan="headers.length" class="pa-3 bg-slate-50 border-bottom">
                    <div class="waste-expanded-drawer">
                      <div v-if="item.direction === 'out'" class="waste-line__help mb-2">
                        <v-icon x-small class="mr-1">fal fa-info-circle</v-icon>
                        {{
                          $t("waste_recovered_help", {
                            qty: wasteNumber(item.qty, 3),
                            saved: wasteNumber(item.recovered_qty || 0, 3),
                            lost: wasteNumber(netQty(item), 3),
                          })
                        }}
                      </div>

                      <v-row dense>
                        <v-col v-if="item.direction === 'out'" cols="12" sm="3">
                          <v-text-field
                            v-model.number="item.recovered_qty"
                            type="number"
                            min="0"
                            :step="item.integerOnly ? 1 : 0.001"
                            :label="$t('waste_recovered_qty')"
                            outlined
                            dense
                            hide-details
                            class="bg-white rounded-lg text-caption"
                          />
                        </v-col>
                        <v-col cols="12" :sm="item.direction === 'out' ? 3 : 4">
                          <v-text-field
                            v-model="item.lot_no"
                            :label="$t('waste_lot_no')"
                            outlined
                            dense
                            hide-details
                            maxlength="50"
                            class="bg-white rounded-lg text-caption"
                          />
                        </v-col>
                        <v-col cols="12" :sm="item.direction === 'out' ? 3 : 4">
                          <div class="d-flex align-center">
                            <DatePicker
                              v-model="item.expired_date"
                              :dense="true"
                              :placeholder="false"
                              label="waste_expired_date"
                              hide-details
                              class="flex-grow-1"
                            />
                            <v-btn
                              v-if="item.expired_date"
                              icon
                              x-small
                              class="ml-1"
                              :aria-label="$t('waste_clear_date')"
                              @click="item.expired_date = ''"
                            >
                              <v-icon x-small>fal fa-times</v-icon>
                            </v-btn>
                          </div>
                        </v-col>
                        <v-col cols="12" :sm="item.direction === 'out' ? 3 : 4">
                          <v-text-field
                            v-model="item.reason"
                            :label="$t('reason')"
                            outlined
                            dense
                            hide-details
                            maxlength="255"
                            class="bg-white rounded-lg text-caption"
                          />
                        </v-col>
                        <v-col cols="12">
                          <v-text-field
                            v-model="item.note"
                            :label="$t('note')"
                            outlined
                            dense
                            hide-details
                            maxlength="255"
                            class="bg-white rounded-lg text-caption"
                          />
                        </v-col>
                      </v-row>

                      <div
                        v-if="showErrors && lineError(item)"
                        class="error--text text-caption mt-2 font-weight-bold d-flex align-center"
                        role="alert"
                      >
                        <v-icon x-small color="error" class="mr-1">fal fa-exclamation-circle</v-icon>
                        {{ lineError(item) }}
                      </div>
                    </div>
                  </td>
                </template>

                <!-- Empty State -->
                <template slot="no-data">
                  <div class="ps-empty text-center py-12">
                    <v-icon size="56" class="ps-empty-icon mb-2">fal fa-hand-pointer</v-icon>
                    <div class="ps-empty-text font-weight-bold">
                      {{ $t("waste_lines_empty_title") }}
                    </div>
                    <div class="ps-empty-subtext mt-1 text-caption">
                      {{ $t("waste_lines_empty_hint") }}
                    </div>
                  </div>
                </template>
              </v-data-table>
            </div>

            <!-- Bottom HUD (Financial & Evidence Section) -->
            <div class="cart-summary-area pa-3 border-top shrink-0">
              <!-- Evidence & Record Date Grid -->
              <v-card outlined elevation="0" class="ap-card pa-3 mb-3 bg-slate-50">
                <v-row dense>
                  <!-- Evidence Photo Uploader -->
                  <v-col cols="12" md="6">
                    <div class="d-flex align-center mb-1">
                      <span class="font-weight-bold text-caption">{{ $t("waste_evidence") }}</span>
                      <span class="error--text ml-1">*</span>
                    </div>
                    <WasteImageUploader v-model="images" :disabled="submitting || !canCreate" />
                    <div
                      v-if="showErrors && !images.length"
                      class="error--text text-caption mt-1 font-weight-bold d-flex align-center"
                      role="alert"
                    >
                      <v-icon x-small color="error" class="mr-1">fal fa-exclamation-circle</v-icon>
                      {{ $t("waste_photo_required") }}
                    </div>
                  </v-col>

                  <!-- Date & Notes -->
                  <v-col cols="12" md="6">
                    <div class="d-flex flex-column" style="gap: 10px;">
                      <div>
                        <DatePicker
                          v-model="recordDate"
                          :max="today"
                          :dense="true"
                          :placeholder="false"
                          label="date"
                          hide-details
                        />
                      </div>
                      <div>
                        <TextArea
                          v-model="note"
                          label="note"
                          :rows="2"
                          :dense="true"
                        />
                      </div>
                    </div>
                  </v-col>
                </v-row>
              </v-card>

              <!-- Metrics Summary Cards & Action Submit Button -->
              <v-row dense align="center" justify="space-between">
                <!-- Summary Metrics Grid -->
                <v-col cols="12" lg="8">
                  <v-row dense>
                    <!-- Metric 1: Items -->
                    <v-col cols="4">
                      <div class="ps-summary-card">
                        <div class="ps-summary-icon ps-summary-icon--primary">
                          <v-icon small>fal fa-boxes</v-icon>
                        </div>
                        <div class="ps-summary-meta">
                          <div class="ps-summary-label">{{ $t("items") }}</div>
                          <div class="ps-summary-value text-truncate">{{ lines.length }}</div>
                        </div>
                      </div>
                    </v-col>

                    <!-- Metric 2: Estimated Cost -->
                    <v-col cols="4">
                      <div class="ps-summary-card">
                        <div class="ps-summary-icon ps-summary-icon--danger">
                          <v-icon small>fal fa-arrow-down</v-icon>
                        </div>
                        <div class="ps-summary-meta">
                          <div class="ps-summary-label">{{ $t("waste_estimated_cost") }}</div>
                          <div class="ps-summary-value ps-summary-value--danger text-truncate">
                            {{ wasteNumber(estimatedCost) }} ₭
                          </div>
                        </div>
                      </div>
                    </v-col>

                    <!-- Metric 3: Lost Sale Value -->
                    <v-col cols="4">
                      <div class="ps-summary-card">
                        <div class="ps-summary-icon ps-summary-icon--info">
                          <v-icon small>fal fa-arrow-up</v-icon>
                        </div>
                        <div class="ps-summary-meta">
                          <div class="ps-summary-label">{{ $t("waste_lost_sale_value") }}</div>
                          <div class="ps-summary-value ps-summary-value--info text-truncate">
                            {{ wasteNumber(lostSaleValue) }} ₭
                          </div>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                </v-col>

                <!-- Submit Button -->
                <v-col cols="12" lg="4" class="d-flex align-center justify-end">
                  <v-btn
                    color="primary"
                    large
                    depressed
                    :loading="submitting"
                    :disabled="!canCreate || !lines.length"
                    @click="submit"
                    class="font-weight-bold px-5 rounded-lg flex-grow-1 flex-lg-grow-0"
                  >
                    <v-icon left small>fal fa-paper-plane</v-icon>
                    {{ $t("waste_submit") }}
                  </v-btn>
                </v-col>
              </v-row>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <app-loading :overlay="submitting" />
  </div>
</template>

<script>
import _ from "lodash";
import moment from "moment";
import { myMixin } from "@/mixin/main";
import { wasteMixin } from "@/mixin/waste";
import WasteImageUploader from "@/components/waste/WasteImageUploader";
import WasteItemTypeTag from "@/components/waste/WasteItemTypeTag";
import TextArea from "@/components/global/TextArea";
import TextSelect from "@/components/global/TextSelect";
import { confirm, toast, success } from "@/controllers/alert";
import * as waste from "@/utils/waste";

// What lineProblem()/qtyProblem() codes mean to the person filling the form
const PROBLEM_MESSAGES = {
  invalid: "waste_qty_invalid",
  range: "qty_out_of_range",
  whole: "waste_whole_number_only",
  type: "waste_select_type_required",
  recovered: "recovered_qty_invalid",
  stock: "waste_stock_not_enough",
};

export default {
  name: "WasteManagement",

  mixins: [myMixin, wasteMixin],

  components: { WasteImageUploader, WasteItemTypeTag, TextArea, TextSelect },

  data() {
    return {
      docType: "waste",
      source: "products",
      search: "",
      loadingItems: true,
      products: [],
      ingredients: [],
      lines: [],
      expandedLines: [],
      images: [],
      note: "",
      today: moment().format("YYYY-MM-DD"),
      recordDate: moment().format("YYYY-MM-DD"),
      submitting: false,
      showErrors: false,
      scannedKey: null,
      submitKey: this.newSubmitKey(),
    };
  },

  computed: {
    canCreate() {
      return this.MyRole("create_waste");
    },

    pickerRows() {
      return this.source === "products" ? this.products : this.ingredients;
    },

    recordHeaders() {
      const h = [
        { text: "#", value: "index", align: "center", sortable: false, width: "45px" },
        { text: this.$t("name"), value: "name", sortable: false },
      ];
      if (this.docType === "wrong_usage") {
        h.push({
          text: this.$t("waste_doc_wrong_usage"),
          value: "direction",
          align: "center",
          sortable: false,
          width: "130px",
        });
      }
      h.push(
        { text: this.$t("quantity"), value: "qty", align: "center", sortable: false, width: "135px" },
        { text: this.$t("waste_type"), value: "waste_type", sortable: false, width: "165px" },
        { text: this.$t("waste_estimated_cost"), value: "cost", align: "end", sortable: false, width: "130px" },
        { text: this.$t("manage"), value: "actions", align: "center", sortable: false, width: "90px" }
      );
      return h;
    },

    hasUnsavedWork() {
      return !this.submitting && (this.lines.length > 0 || this.images.length > 0 || !!this.note);
    },

    estimatedCost() {
      return this.lines.reduce((sum, line) => {
        const cost = this.lineCost(line);
        return sum + (line.direction === "in" ? -cost : cost);
      }, 0);
    },

    lostSaleValue() {
      return this.lines.reduce((sum, line) => {
        if (line.direction === "in") return sum;
        return sum + this.netQty(line) * (Number(line.price_sale) || 0);
      }, 0);
    },
  },

  watch: {
    source() {
      this.debouncedFetch.cancel();
      this.search = "";
      this.fetchItems("");
    },

    search() {
      if (this.search === this.lastTerm) return;
      this.debouncedFetch();
    },

    docType(type) {
      if (type === "waste") {
        this.lines.forEach((line) => {
          line.direction = "out";
        });
      } else {
        this.lines.forEach((line) => {
          if (!line.waste_type) line.waste_type = "wrong_usage";
        });
      }
    },
  },

  created() {
    this.fetchSeq = 0;
    this.lastTerm = "";
    this.scanDetector = waste.createScanDetector();
    this.debouncedFetch = _.debounce(() => this.fetchItems(), 400);
    this.fetchItems();
  },

  mounted() {
    if (!this.$vuetify.breakpoint.smAndDown) this.$nextTick(this.focusSearch);
    window.addEventListener("keydown", this.onGlobalKey, true);
    window.addEventListener("beforeunload", this.onBeforeUnload);
  },

  beforeDestroy() {
    this.debouncedFetch.cancel();
    clearTimeout(this.flashTimer);
    window.removeEventListener("keydown", this.onGlobalKey, true);
    window.removeEventListener("beforeunload", this.onBeforeUnload);
  },

  beforeRouteLeave(to, from, next) {
    if (!this.hasUnsavedWork) {
      next();
      return;
    }
    confirm({ props: this, text: "waste_leave_confirm" }).then(({ isConfirmed }) => {
      if (isConfirmed) next();
      else next(false);
    });
  },

  methods: {
    round3(val) {
      return waste.round3(val);
    },

    focusSearch() {
      const input = this.searchInput();
      if (input) {
        input.focus();
        input.select();
      }
    },

    onBeforeUnload(event) {
      if (!this.hasUnsavedWork) return undefined;
      event.preventDefault();
      event.returnValue = "";
      return "";
    },

    async onSearch(term) {
      this.debouncedFetch.cancel();
      const applied = await this.fetchItems(term);
      if (applied) this.autoAddScanned(term);
    },

    autoAddScanned(term) {
      if (!String(term || "").trim() || !this.canCreate) return;
      const match = waste.matchScan(this.pickerRows, term);
      if (match.kind === "one") {
        this.scanAdd(match.row);
        this.search = "";
        this.$nextTick(this.focusSearch);
        return;
      }

      if (match.kind === "many") {
        this.notify("waste_scan_multiple", { code: term }, "warning");
      } else if (!this.pickerRows.length) {
        this.notify("waste_scan_not_found", { code: term }, "error");
        this.$nextTick(this.focusSearch);
      }
    },

    onGlobalKey(event) {
      if (!this.canCreate || this.submitting) return;
      if (document.querySelector(".swal2-container, .v-dialog--active")) return;

      const target = event.target;
      if (target && target === this.searchInput()) return;

      const isField =
        target &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable);
      const hit = this.scanDetector.key(
        event,
        window.performance.now(),
        () => (isField ? { el: target, value: target.value } : null)
      );
      if (!hit) return;

      event.preventDefault();
      event.stopPropagation();

      const before = hit.snapshot;
      if (before && before.el && before.el.value !== before.value) {
        before.el.value = before.value;
        before.el.dispatchEvent(new Event("input", { bubbles: true }));
      }

      this.search = hit.code;
      this.onSearch(hit.code);
    },

    searchInput() {
      const box = this.$refs.search && this.$refs.search.$el;
      return box ? box.querySelector("input") : null;
    },

    scanAdd(row) {
      const existing = this.lines.find((line) => line.key === row.key);
      if (existing) {
        existing.qty = (Number(existing.qty) || 0) + 1;
        this.notify("waste_scan_qty_increased", { name: row.name }, "info");
      } else {
        this.addRow(row);
        this.notify("waste_scan_added", { name: row.name }, "success");
      }
      this.flashLine(row.key);
    },

    flashLine(key) {
      this.scannedKey = key;
      clearTimeout(this.flashTimer);
      this.flashTimer = setTimeout(() => {
        this.scannedKey = null;
      }, 1600);
    },

    notify(key, params, type) {
      this.$toast.open({ type, message: this.$t(key, params) });
    },

    async fetchItems(term) {
      if (!this.canCreate) {
        this.loadingItems = false;
        return false;
      }

      const seq = ++this.fetchSeq;
      this.loadingItems = true;
      const text = typeof term === "string" ? term : this.search;
      this.lastTerm = text || "";
      const q = encodeURIComponent(text || "");
      let applied = false;
      try {
        if (this.source === "products") {
          const { data } = await this.$http.get(
            `stock_waste_products?q=${q}&per_page=50`
          );
          if (seq === this.fetchSeq && data.status) {
            this.products = data.data || [];
            applied = true;
          }
        } else {
          const { data } = await this.$http.get(
            `ingredients_paginate?q=${q}&per_page=50`
          );
          if (seq === this.fetchSeq && data.status) {
            this.ingredients = (data.data.data || []).map((item) => ({
              key: `i-${item.id}`,
              ingredient_id: item.id,
              item_type: "ingredient",
              name: this.ingredientName(item),
              sku: item.code,
              img: item.img,
              unit: item.unit_name,
              stock: Number(item.current_stock) || 0,
              price_buy: Number(item.unit_cost) || 0,
              price_sale: null,
            }));
            applied = true;
          }
        }
      } catch (err) {
        // the axios interceptor already told the user what went wrong
      } finally {
        if (seq === this.fetchSeq) this.loadingItems = false;
      }
      return applied;
    },

    clearSearch() {
      this.debouncedFetch.cancel();
      this.search = "";
      this.fetchItems("");
    },

    ingredientName(item) {
      const locale = this.$i18n.locale;
      if (locale === "en" && item.name_en) return item.name_en;
      if (locale === "th" && item.name_th) return item.name_th;
      if ((locale === "cn" || locale === "ch") && item.name_cn) {
        return item.name_cn;
      }
      return item.name_la || item.name_en || item.name_th || item.name_cn || "-";
    },

    codeLabel(row) {
      return waste.codeLabel(row);
    },

    inRecord(key) {
      return this.lines.some((line) => line.key === key);
    },

    addRow(row) {
      if (!this.canCreate) return;
      if (this.inRecord(row.key)) {
        toast(this, "item_already_in_list", "warning");
        return;
      }

      this.lines.push({
        key: row.key,
        item_type: row.item_type,
        product_id: row.product_id || null,
        variant_id: row.variant_id || null,
        ingredient_id: row.ingredient_id || null,
        name: row.name,
        sub: this.codeLabel(row),
        variant_label: row.variant_label || "",
        img: row.img,
        unit: row.unit_name || row.unit || "",
        stock: Number(row.stock) || 0,
        unit_cost: Number(row.price_buy) || 0,
        price_sale: Number(row.price_sale) || 0,
        integerOnly: row.item_type !== "ingredient",
        direction: "out",
        qty: 1,
        recovered_qty: 0,
        waste_type: this.docType === "wrong_usage" ? "wrong_usage" : "damaged",
        reason: "",
        lot_no: "",
        expired_date: "",
        note: "",
      });
    },

    decreaseQty(item) {
      const step = item.integerOnly ? 1 : 0.1;
      const nextQty = this.round3(Number(item.qty || 0) - step);
      if (nextQty < 1) {
        this.removeLine(item.key);
      } else {
        item.qty = nextQty;
      }
    },

    increaseQty(item) {
      const step = item.integerOnly ? 1 : 0.1;
      item.qty = this.round3(Number(item.qty || 0) + step);
    },

    removeLine(key) {
      this.lines = this.lines.filter((line) => line.key !== key);
      this.expandedLines = this.expandedLines.filter((line) => line.key !== key);
    },

    clearAllLines() {
      this.lines = [];
      this.expandedLines = [];
    },

    isLineExpanded(item) {
      return this.expandedLines.some((line) => line.key === item.key);
    },

    toggleLineExpand(item) {
      const idx = this.expandedLines.findIndex((line) => line.key === item.key);
      if (idx >= 0) {
        this.expandedLines.splice(idx, 1);
      } else {
        this.expandedLines.push(item);
      }
    },

    netQty(line) {
      return waste.netQty(line);
    },

    lineCost(line) {
      return waste.netQty(line) * (Number(line.unit_cost) || 0);
    },

    stockAfter(line) {
      return waste.stockAfter(line);
    },

    problemMessage(code) {
      const key = PROBLEM_MESSAGES[code];
      return key ? this.$t(key) : "";
    },

    qtyError(line) {
      return this.problemMessage(waste.qtyProblem(line));
    },

    lineError(line) {
      return this.problemMessage(waste.lineProblem(line));
    },

    async submit() {
      this.showErrors = true;
      if (!this.canCreate) return;

      if (!this.lines.length) {
        toast(this, "waste_lines_empty_title", "warning");
        return;
      }
      if (this.lines.some((line) => this.lineError(line))) {
        toast(this, "waste_fix_errors", "warning");
        return;
      }
      if (!this.images.length) {
        toast(this, "waste_photo_required", "warning");
        return;
      }

      const { isConfirmed } = await confirm({
        props: this,
        text: "waste_confirm_submit",
      });
      if (!isConfirmed) return;

      const empty = (value) => (value === "" || value === undefined ? null : value);
      const data = this.lines.map((line) => ({
        product_id: line.product_id,
        variant_id: line.variant_id,
        ingredient_id: line.ingredient_id,
        direction: line.direction,
        qty: Number(line.qty),
        recovered_qty: line.direction === "out" ? Number(line.recovered_qty) || 0 : 0,
        waste_type: line.waste_type,
        reason: empty(line.reason),
        lot_no: empty(line.lot_no),
        expired_date: empty(line.expired_date),
        note: empty(line.note),
      }));

      const form = new FormData();
      form.append("doc_type", this.docType);
      form.append("date", this.recordDate);
      form.append("source", "web");
      form.append("idempotency_key", this.submitKey);
      if (this.note) form.append("note", this.note);
      form.append("data", JSON.stringify(data));
      this.images.forEach((file) => form.append("images[]", file));

      try {
        this.submitting = true;
        const { data: res } = await this.$http.post("stock_waste", form);
        if (res.status) {
          this.resetForm();
          await success(this, res.message);
        }
      } catch (err) {
        // the axios interceptor already showed the API's message
      } finally {
        this.submitting = false;
      }
    },

    newSubmitKey() {
      const c = window.crypto;
      if (c && typeof c.randomUUID === "function") return c.randomUUID();
      return `k${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
    },

    resetForm() {
      this.submitKey = this.newSubmitKey();
      this.lines = [];
      this.expandedLines = [];
      this.images = [];
      this.note = "";
      this.recordDate = this.today;
      this.showErrors = false;
    },
  },
};
</script>

<style scoped>
.purchase-page-wrapper {
  height: calc(100vh - 65px);
  display: flex;
  flex-direction: column;
}

.purchase-page-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 16px;
  overflow: hidden;
}

.cart-content-area {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  border-radius: 12px;
}

/* PosSmart Section Cards (matches purchase.vue & add-product.vue) */
.ap-card {
  border-radius: 16px !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: none !important;
  overflow: hidden;
  background-color: #ffffff !important;
}

.ap-card-title {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  font-size: 14px;
  font-weight: 600;
}

.ap-card-accent {
  width: 3px;
  height: 15px;
  border-radius: 2px;
  background: var(--v-primary-base, #5b39c9);
  display: inline-block;
  flex-shrink: 0;
}

.ap-card-subtitle {
  font-size: 11.5px;
  font-weight: 400;
  color: #94a3b8;
}

.cart-summary-area {
  flex-shrink: 0;
  background-color: #ffffff;
  color: inherit;
  display: flex;
  flex-direction: column;
}

.product-card {
  height: 100%;
  min-height: 0;
}

.product-card >>> .v-card__text {
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.purchase-main-card {
  height: 100%;
  min-height: 0;
}

.filter-controls {
  flex-shrink: 0;
}

.product-list-container {
  flex: 1 1 0;
  min-height: 0;
  height: 100%;
  overflow-y: auto;
  border-radius: 12px;
  padding: 2px 4px 4px 2px;
}

.border-bottom {
  border-bottom: 1px solid #e2e8f0 !important;
}

.border-top {
  border-top: 1px solid #e2e8f0 !important;
}

.bg-slate-50 {
  background-color: #f8fafc !important;
}

.waste-source-tabs-wrap {
  width: 100%;
}

.waste-source-tabs-wrap .br-tabs {
  display: flex;
  width: 100%;
  height: 38px;
  box-sizing: border-box;
  align-items: center;
  gap: 4px;
  padding: 3px;
  border-radius: 10px;
  background: #f1f2f7;
  border: 1px solid #e2e8f0;
}

.waste-source-tabs-wrap .br-tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  padding: 4px 10px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  user-select: none;
  color: #64748b;
  transition: all 0.15s ease;
}

.waste-source-tabs-wrap .br-tab:hover {
  color: #141021;
}

.waste-source-tabs-wrap .br-tab--active {
  color: #5b39c9 !important;
  background: #ffffff !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  font-weight: 700;
}

.theme--dark .waste-source-tabs-wrap .br-tabs {
  background: #1b1d30;
  border-color: rgba(154, 140, 240, 0.16);
}

.theme--dark .waste-source-tabs-wrap .br-tab {
  color: #7c82a0;
}

.theme--dark .waste-source-tabs-wrap .br-tab--active {
  color: #9a8cf0 !important;
  background: rgba(154, 140, 240, 0.18) !important;
}

/* ===================================================
   Product Items in Left Panel (matches purchase.vue)
   =================================================== */
.product-item-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;
  gap: 8px;
}

.product-item-card:hover {
  border-color: #5b39c9;
  box-shadow: 0 4px 12px rgba(91, 57, 201, 0.08);
  background-color: rgba(91, 57, 201, 0.02);
}

.product-item-card:hover .product-name-text {
  color: #5b39c9;
}

.product-item-card:hover .product-add-btn {
  background-color: #5b39c9;
  color: #ffffff;
}

.product-item-card:hover .product-add-btn .add-btn-icon {
  color: #ffffff !important;
}

.product-item-card.is-in-cart {
  background-color: rgba(91, 57, 201, 0.05);
  border-color: #5b39c9;
}

.product-item-card.is-disabled {
  opacity: 0.6;
  pointer-events: none;
}

.product-card-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1 1 auto;
}

.product-thumb-box {
  position: relative;
  width: 46px;
  height: 46px;
  flex: 0 0 46px;
}

.product-thumb-img {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  object-fit: cover;
  background: #f1f5f9;
}

.cart-count-badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: #5b39c9;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  min-width: 18px;
  height: 18px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #ffffff;
}

.product-info-box {
  min-width: 0;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.product-name-text {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.3;
}

.product-sub-row {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
}

.product-code-mono {
  font-family: monospace;
  color: #94a3b8;
}

.product-dot-sep {
  color: #cbd5e1;
}

.stock-pill {
  font-size: 10.5px;
  padding: 1px 6px;
  border-radius: 999px;
  font-weight: 600;
  white-space: nowrap;
}

.stock-pill-out {
  background: #fef2f2;
  color: #ef4444;
}

.stock-pill-low {
  background: #fffbeb;
  color: #d97706;
}

.stock-pill-normal {
  background: #f0fdf4;
  color: #16a34a;
}

.product-price-row {
  display: flex;
  align-items: center;
  gap: 4px;
}

.buy-price-val {
  font-size: 12.5px;
  font-weight: 700;
  color: #5b39c9;
  font-variant-numeric: tabular-nums;
}

.product-add-btn {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #64748b;
  flex: 0 0 28px;
  transition: all 0.15s ease;
  outline: none;
}

.product-add-btn .add-btn-icon {
  color: #64748b;
  transition: color 0.15s ease;
}

.product-add-btn:hover {
  background: #5b39c9;
  border-color: #5b39c9;
  color: #ffffff;
}

.product-add-btn:hover .add-btn-icon {
  color: #ffffff !important;
}

.product-add-btn.btn-in-cart {
  background: #5b39c9;
  border-color: #5b39c9;
  color: #ffffff;
}

.product-add-btn.btn-in-cart .add-btn-icon {
  color: #ffffff !important;
}

/* ===================================================
   Table Styling (matches purchase.vue)
   =================================================== */
.purchase-table >>> .v-data-table__wrapper table {
  border-collapse: collapse;
}

.purchase-table >>> .v-data-table__wrapper table thead th {
  color: #64748b;
  font-weight: 700;
  font-size: 10.5px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background-color: transparent !important;
  background: transparent !important;
  box-shadow: none !important;
  border-bottom: 1px solid #e2e8f0;
  height: 38px;
  white-space: nowrap;
}

.purchase-table >>> .v-data-table__wrapper table tbody tr {
  transition: background-color 0.15s ease;
}

.purchase-table >>> .v-data-table__wrapper table tbody tr:hover {
  background-color: #f8fafc !important;
}

.purchase-table >>> .v-data-table__wrapper table tbody td {
  border-bottom: 1px solid #f1f5f9;
  padding: 8px 12px;
}

.ps-row-index {
  font-size: 12px;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
}

.ps-row-thumb {
  position: relative;
  width: 38px;
  height: 38px;
  display: inline-flex;
}

.ps-thumb-avatar {
  background: #f1f5f9 !important;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.ps-row-name {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.ps-product-name {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ps-product-code {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-family: monospace;
  color: #94a3b8;
}

/* Quantity Stepper */
.ps-qty-stepper {
  display: inline-flex;
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 2px;
  gap: 2px;
}

.error-border {
  border-color: #ef4444 !important;
}

.ps-qty-btn {
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: none;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
}

.ps-qty-btn:hover {
  color: #5b39c9;
  background: rgba(91, 57, 201, 0.08);
}

.ps-qty-btn--plus {
  color: #5b39c9;
}

.ps-qty-input {
  width: 44px;
  height: 24px;
  border: none;
  background: transparent;
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #1e293b;
  outline: none;
}

.ps-total-value {
  font-size: 13.5px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #5b39c9;
}

.ps-row-actions {
  display: flex;
  align-items: center;
  gap: 5px;
}

.ps-act {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: #64748b;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
}

.ps-act:hover {
  color: #5b39c9;
  border-color: #5b39c9;
  background: rgba(91, 57, 201, 0.06);
}

.primary-border {
  border-color: #5b39c9 !important;
}

.ps-act--danger:hover {
  color: #ef4444;
  border-color: #ef4444;
  background: #fef2f2;
}

.waste-expanded-drawer {
  padding: 6px 4px;
}

.waste-line__help {
  font-size: 11px;
  color: #64748b;
}

/* Empty State */
.ps-empty {
  color: #94a3b8;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.ps-empty-icon {
  color: #cbd5e1 !important;
}

.ps-empty-text {
  font-size: 14px;
  color: #475569;
}

.ps-empty-subtext {
  font-size: 12px;
  color: #94a3b8;
}

/* ===================================================
   Bottom HUD: PosSmart Summary Metrics Cards
   =================================================== */
.ps-summary-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  transition: all 0.15s ease;
  min-width: 0;
}

.ps-summary-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.ps-summary-icon {
  width: 36px;
  height: 36px;
  flex: 0 0 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
}

.ps-summary-icon--primary {
  background: rgba(91, 57, 201, 0.1);
  color: #5b39c9;
  border-color: rgba(91, 57, 201, 0.2);
}

.ps-summary-icon--danger {
  background: #fef2f2;
  color: #dc2626;
  border-color: #fecdd3;
}

.ps-summary-icon--info {
  background: #f0fdf4;
  color: #0284c7;
  border-color: #bae6fd;
}

.ps-summary-meta {
  min-width: 0;
  flex: 1 1 auto;
}

.ps-summary-label {
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #94a3b8;
}

.ps-summary-value {
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}

.ps-summary-value--danger {
  color: #dc2626;
}

.ps-summary-value--info {
  color: #0284c7;
}

.waste-type-select >>> .v-text-field__details {
  margin-bottom: 0px !important;
}

.waste-type-select >>> .v-input__slot {
  margin-bottom: 0px !important;
}

.waste-segment-toggle >>> .v-btn--active {
  background-color: var(--v-primary-base, #5b39c9) !important;
  color: #ffffff !important;
}

/* ===================================================
   Dark Theme Overrides (matches purchase.vue & /products)
   =================================================== */
.theme--dark .ap-card {
  border-color: rgba(255, 255, 255, 0.08) !important;
  background-color: #1e1e2d !important;
}

.theme--dark .ap-card-title {
  color: #ffffff;
}

.theme--dark .ap-card-subtitle {
  color: #94a3b8;
}

.theme--dark .cart-summary-area {
  background-color: #1e1e2d !important;
  border-top-color: rgba(255, 255, 255, 0.08) !important;
}

.theme--dark .border-bottom {
  border-bottom-color: rgba(255, 255, 255, 0.08) !important;
}

.theme--dark .border-top {
  border-top-color: rgba(255, 255, 255, 0.08) !important;
}

.theme--dark .bg-slate-50 {
  background-color: #262638 !important;
}

.theme--dark .product-item-card {
  background-color: #1e1e2d;
  border-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .product-item-card:hover {
  border-color: #6366f1;
  background-color: #252538;
}

.theme--dark .product-item-card.is-in-cart {
  background-color: rgba(91, 57, 201, 0.2);
  border-color: #818cf8;
}

.theme--dark .product-name-text {
  color: #f1f5f9;
}

.theme--dark .product-thumb-img {
  background: #262638;
}

.theme--dark .cart-count-badge {
  border-color: #1e1e2d;
}

.theme--dark .buy-price-val {
  color: #a5b4fc;
}

.theme--dark .product-add-btn {
  background-color: #262638;
  border-color: rgba(255, 255, 255, 0.1);
  color: #94a3b8;
}

.theme--dark .product-add-btn .add-btn-icon {
  color: #94a3b8;
}

.theme--dark .stock-pill-out {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
}

.theme--dark .stock-pill-low {
  background: rgba(245, 158, 11, 0.15);
  color: #fbbf24;
}

.theme--dark .stock-pill-normal {
  background: rgba(16, 185, 129, 0.15);
  color: #34d399;
}

.theme--dark .purchase-table >>> .v-data-table__wrapper table thead th {
  color: #94a3b8;
  background-color: transparent !important;
  background: transparent !important;
  box-shadow: none !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.theme--dark .purchase-table >>> .v-data-table__wrapper table tbody tr:hover {
  background-color: rgba(255, 255, 255, 0.03) !important;
}

.theme--dark .purchase-table >>> .v-data-table__wrapper table tbody td {
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  color: #f1f5f9;
}

.theme--dark .ps-thumb-avatar {
  background: #262638 !important;
  border-color: rgba(255, 255, 255, 0.1);
}

.theme--dark .ps-product-name {
  color: #f1f5f9;
}

.theme--dark .ps-qty-stepper {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .ps-qty-btn {
  background: #1e1e2d;
  color: #94a3b8;
}

.theme--dark .ps-qty-btn--plus {
  color: #a5b4fc;
}

.theme--dark .ps-qty-input {
  color: #ffffff;
}

.theme--dark .ps-total-value {
  color: #a5b4fc;
}

.theme--dark .ps-act {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.1);
  color: #94a3b8;
}

.theme--dark .ps-act:hover {
  color: #a5b4fc;
  border-color: #818cf8;
  background: rgba(129, 140, 248, 0.15);
}

.theme--dark .ps-act--danger:hover {
  color: #f87171;
  border-color: #ef4444;
  background: rgba(239, 68, 68, 0.15);
}

.theme--dark .ps-empty-icon {
  color: #475569 !important;
}

.theme--dark .ps-empty-text {
  color: #cbd5e1;
}

.theme--dark .ps-summary-card {
  background: #262638;
  border-color: rgba(255, 255, 255, 0.08);
}

.theme--dark .ps-summary-card:hover {
  border-color: rgba(255, 255, 255, 0.16);
}

.theme--dark .ps-summary-icon--primary {
  background: rgba(129, 140, 248, 0.15);
  color: #a5b4fc;
  border-color: rgba(129, 140, 248, 0.25);
}

.theme--dark .ps-summary-icon--danger {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
  border-color: rgba(239, 68, 68, 0.25);
}

.theme--dark .ps-summary-icon--info {
  background: rgba(56, 189, 248, 0.15);
  color: #38bdf8;
  border-color: rgba(56, 189, 248, 0.25);
}

.theme--dark .ps-summary-value {
  color: #ffffff;
}

.theme--dark .ps-summary-value--danger {
  color: #f87171;
}

.theme--dark .ps-summary-value--info {
  color: #38bdf8;
}

@media (max-width: 960px) {
  .purchase-page-wrapper {
    height: auto;
  }

  .purchase-page-container {
    flex: none;
  }

  .cart-content-area {
    flex: none;
    height: auto;
    max-height: 50vh;
  }

  .product-list-container {
    max-height: 380px;
  }
}
</style>
